import { createContext, useContext, useState, useCallback, useMemo } from "react";

const BetSlipContext = createContext(null);

/**
 * BetSlipProvider — global state for the "Add to Slip" basket flow.
 *
 * A pick is identified by `${fixture_id}-${market_type}-${prediction}` so
 * the same match can have multiple picks (1X2 + BTTS + O/U for example).
 *
 * Stored pick shape:
 *   { id, fixture_id, home_team, away_team, league, kickoff,
 *     market_type, prediction, confidence, risk_level, odds? }
 */
export const BetSlipProvider = ({ children }) => {
  const [picks, setPicks] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const pickId = (fid, market, prediction) =>
    `${fid}-${market}-${prediction}`;

  const isPicked = useCallback(
    (fid, market, prediction) =>
      picks.some((p) => p.id === pickId(fid, market, prediction)),
    [picks]
  );

  const addPick = useCallback((pick) => {
    setPicks((prev) => {
      const id = pickId(pick.fixture_id, pick.market_type, pick.prediction);
      if (prev.some((p) => p.id === id)) return prev;
      return [...prev, { ...pick, id }];
    });
    setDrawerOpen(true);
  }, []);

  const removePick = useCallback((id) => {
    setPicks((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const togglePick = useCallback((pick) => {
    const id = pickId(pick.fixture_id, pick.market_type, pick.prediction);
    setPicks((prev) => {
      if (prev.some((p) => p.id === id)) {
        return prev.filter((p) => p.id !== id);
      }
      return [...prev, { ...pick, id }];
    });
  }, []);

  const clear = useCallback(() => setPicks([]), []);

  const value = useMemo(
    () => ({
      picks,
      count: picks.length,
      isPicked,
      addPick,
      removePick,
      togglePick,
      clear,
      drawerOpen,
      setDrawerOpen,
    }),
    [picks, isPicked, addPick, removePick, togglePick, clear, drawerOpen]
  );

  return <BetSlipContext.Provider value={value}>{children}</BetSlipContext.Provider>;
};

export const useBetSlip = () => {
  const ctx = useContext(BetSlipContext);
  if (!ctx) throw new Error("useBetSlip must be used inside <BetSlipProvider>");
  return ctx;
};
